import React, { useContext, useEffect, useMemo, useState } from 'react';
import { MainLanguageContext } from '../context/MainLanguageContext';
import useGet from '../customHooks/useGet';

const labelize = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};
const emptyFilters = { search: '', promo_code: '', promo_code_id: '', booking_id: '', from_date: '', to_date: '' };

const PromoRedemptions = () => {
  const { mainLanguage } = useContext(MainLanguageContext);
  const [response, getRedemptions] = useGet();
  const [filters, setFilters] = useState(emptyFilters);

  useEffect(() => {
    if (mainLanguage) getRedemptions(`promo_codes/redemptions/${mainLanguage}?per_page=20`);
  }, [mainLanguage]);

  const searchRedemptions = (event) => {
    event.preventDefault();
    const params = new URLSearchParams({ per_page: '20' });
    Object.entries(filters).forEach(([key, value]) => {
      if (value.trim()) params.set(key, value.trim());
    });
    getRedemptions(`promo_codes/redemptions/${mainLanguage}?${params.toString()}`);
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    if (mainLanguage) getRedemptions(`promo_codes/redemptions/${mainLanguage}?per_page=20`);
  };

  const updateFilter = (event) => setFilters((current) => ({ ...current, [event.target.name]: event.target.value }));

  const rows = useMemo(() => {
    const result = response.data?.data;
    return Array.isArray(result) ? result : Array.isArray(result?.data) ? result.data : [];
  }, [response.data]);

  const columns = useMemo(() => rows.length ? Object.keys(rows[0]) : [], [rows]);

  return (
    <section className="pr-10 max-lg:pr-6">
      <div className="mb-4 px-3">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8A98AD]">Promo Code</p>
        <h1 className="mt-1 text-xl font-MluvkaBold text-[#263238]">Redemptions</h1>
        <p className="mt-1 text-sm text-[#7D8CA7]">Review promo code usage and redemption records.</p>
      </div>
      <form onSubmit={searchRedemptions} className="mb-4 rounded-3xl border border-[#E6ECF5] bg-white p-5 shadow-sm max-md:p-4">
        <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-2 max-md:grid-cols-1">
          <label className="text-sm font-medium text-[#52627A]">Search
            <input name="search" value={filters.search} onChange={updateFilter} placeholder="Customer name or email" className="mt-2 h-11 w-full rounded-xl border border-[#DCE4F2] px-4 font-normal outline-none focus:border-primary focus:ring-4 focus:ring-primary/10" />
          </label>
          <label className="text-sm font-medium text-[#52627A]">Promo code
            <input name="promo_code" value={filters.promo_code} onChange={updateFilter} placeholder="e.g. PARTNER50" className="mt-2 h-11 w-full rounded-xl border border-[#DCE4F2] px-4 font-normal outline-none focus:border-primary focus:ring-4 focus:ring-primary/10" />
          </label>
          <label className="text-sm font-medium text-[#52627A]">Promo code ID
            <input type="number" name="promo_code_id" value={filters.promo_code_id} onChange={updateFilter} placeholder="Enter promo code ID" className="mt-2 h-11 w-full rounded-xl border border-[#DCE4F2] px-4 font-normal outline-none focus:border-primary focus:ring-4 focus:ring-primary/10" />
          </label>
          <label className="text-sm font-medium text-[#52627A]">Booking ID
            <input type="number" name="booking_id" value={filters.booking_id} onChange={updateFilter} placeholder="Enter booking ID" className="mt-2 h-11 w-full rounded-xl border border-[#DCE4F2] px-4 font-normal outline-none focus:border-primary focus:ring-4 focus:ring-primary/10" />
          </label>
          <label className="text-sm font-medium text-[#52627A]">From date
            <input type="date" name="from_date" value={filters.from_date} onChange={updateFilter} className="mt-2 h-11 w-full rounded-xl border border-[#DCE4F2] px-4 font-normal outline-none focus:border-primary focus:ring-4 focus:ring-primary/10" />
          </label>
          <label className="text-sm font-medium text-[#52627A]">To date
            <input type="date" name="to_date" value={filters.to_date} onChange={updateFilter} className="mt-2 h-11 w-full rounded-xl border border-[#DCE4F2] px-4 font-normal outline-none focus:border-primary focus:ring-4 focus:ring-primary/10" />
          </label>
          <div className="flex items-end gap-2">
            <button type="submit" disabled={response.isLoading} className="h-11 rounded-full bg-primary px-6 text-sm font-semibold text-white transition hover:brightness-95 disabled:opacity-60">Search</button>
            <button type="button" onClick={clearFilters} className="h-11 rounded-full border border-[#DCE4F2] px-5 text-sm font-medium text-[#52627A] transition hover:bg-[#F6F8FC]">Clear</button>
          </div>
        </div>
      </form>
      <div className="overflow-hidden rounded-3xl border border-[#E6ECF5] bg-white shadow-sm">
        {response.isLoading ? <p className="p-6 text-sm text-[#7D8CA7]">Loading redemptions...</p> : response.error ? (
          <p className="p-6 text-sm text-[#A41139]">Unable to load redemptions. Please try again.</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-[#7D8CA7]">No redemptions found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="bg-[#EFF4FD] text-xs uppercase tracking-wide text-[#71809A]">
                <tr>{columns.map((column) => <th key={column} className="whitespace-nowrap px-5 py-4 font-semibold">{labelize(column)}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-[#EEF1F6] text-[#344054]">
                {rows.map((row, index) => <tr key={row.id ?? index} className="transition hover:bg-[#FAFBFE]">
                  {columns.map((column) => <td key={column} className="max-w-[280px] truncate px-5 py-4">{displayValue(row[column])}</td>)}
                </tr>)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default PromoRedemptions;
